import { type BinauralConfig, type BinauralPlan, createBinauralPlan } from "./binauralPlan"

type BeatGraph = {
  readonly kind: "beat"
  readonly left: OscillatorNode
  readonly right: OscillatorNode
  readonly gain: GainNode
}

type SpatialGraph = {
  readonly kind: "spatial"
  readonly oscillator: OscillatorNode
  readonly panner: PannerNode
  readonly gain: GainNode
}

type ActiveGraph = BeatGraph | SpatialGraph

export class BinauralEngine {
  private active: ActiveGraph | undefined

  constructor(
    private readonly context: AudioContext,
    private readonly destination: AudioNode,
  ) {}

  get playing(): boolean {
    return this.active !== undefined
  }

  start(config: BinauralConfig): BinauralPlan {
    const plan = createBinauralPlan(config)
    this.stop()
    const gain = this.context.createGain()
    gain.gain.setValueAtTime(0, this.context.currentTime)
    gain.connect(this.destination)
    switch (plan.kind) {
      case "beat": {
        const left = this.context.createOscillator()
        const right = this.context.createOscillator()
        const merger = this.context.createChannelMerger(2)
        left.type = right.type = plan.waveform
        left.frequency.value = plan.leftHertz
        right.frequency.value = plan.rightHertz
        left.connect(merger, 0, 0)
        right.connect(merger, 0, 1)
        merger.connect(gain)
        left.start()
        right.start()
        this.active = { kind: "beat", left, right, gain }
        break
      }
      case "spatial": {
        const oscillator = this.context.createOscillator()
        const panner = this.context.createPanner()
        oscillator.type = plan.waveform
        oscillator.frequency.value = plan.carrierHertz
        panner.panningModel = "HRTF"
        panner.distanceModel = "inverse"
        panner.positionX.value = plan.position[0]
        panner.positionY.value = plan.position[1]
        panner.positionZ.value = plan.position[2]
        oscillator.connect(panner).connect(gain)
        oscillator.start()
        this.active = { kind: "spatial", oscillator, panner, gain }
        break
      }
    }
    gain.gain.linearRampToValueAtTime(plan.gain, this.context.currentTime + 0.03)
    return plan
  }

  update(config: BinauralConfig): BinauralPlan {
    const plan = createBinauralPlan(config)
    const active = this.active
    if (active === undefined || active.kind !== plan.kind) return this.start(config)
    active.gain.gain.setTargetAtTime(plan.gain, this.context.currentTime, 0.02)
    switch (plan.kind) {
      case "beat":
        if (active.kind === "beat") {
          active.left.type = active.right.type = plan.waveform
          active.left.frequency.setTargetAtTime(plan.leftHertz, this.context.currentTime, 0.01)
          active.right.frequency.setTargetAtTime(plan.rightHertz, this.context.currentTime, 0.01)
        }
        break
      case "spatial":
        if (active.kind === "spatial") {
          active.oscillator.type = plan.waveform
          active.oscillator.frequency.setTargetAtTime(
            plan.carrierHertz,
            this.context.currentTime,
            0.01,
          )
          active.panner.positionX.setTargetAtTime(plan.position[0], this.context.currentTime, 0.02)
          active.panner.positionY.setTargetAtTime(plan.position[1], this.context.currentTime, 0.02)
          active.panner.positionZ.setTargetAtTime(plan.position[2], this.context.currentTime, 0.02)
        }
        break
    }
    return plan
  }

  stop(): void {
    const active = this.active
    if (active === undefined) return
    const stopAt = this.context.currentTime + 0.08
    active.gain.gain.cancelScheduledValues(this.context.currentTime)
    active.gain.gain.setTargetAtTime(0, this.context.currentTime, 0.012)
    switch (active.kind) {
      case "beat":
        active.left.stop(stopAt)
        active.right.stop(stopAt)
        break
      case "spatial":
        active.oscillator.stop(stopAt)
        break
    }
    this.active = undefined
  }
}
